import { Store } from '@tanstack/react-store'
import type { User, Session } from '@supabase/supabase-js'
import { toast } from 'sonner'
import { supabase } from '../lib/supabase'
import { queryClient } from '../integrations/tanstack-query/root-provider'

export interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  isAdmin: boolean | null // null = not checked yet
}

export const authStore = new Store<AuthState>({
  user: null,
  session: null,
  isLoading: true,
  isAdmin: null,
})

// Initialize auth state
supabase.auth.getSession().then(async ({ data: { session }, error }) => {
  if (error) {
    console.error('Error getting session:', error)
  }

  authStore.setState((state) => ({
    ...state,
    session,
    user: session?.user ?? null,
    isLoading: false,
  }))
})

// Supabase fires SIGNED_IN on every session recovery — initial page load, tab
// refocus, token refresh's _recoverAndRefresh path — not just on actual logins.
// Invalidating every query on each one fans out a refetch storm on flaky
// networks. Only invalidate on a real user→user transition; mount-time fetches
// cover initial load.
let lastKnownUserId: string | null = null

// Listen for auth state changes
supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === 'TOKEN_REFRESHED' && !session) {
    await supabase.auth.signOut()
    toast.error('Session expired. Please log in again.')
  }

  const previousUserId = lastKnownUserId
  const newUserId = session?.user?.id ?? null
  lastKnownUserId = newUserId

  authStore.setState((state) => ({
    ...state,
    session,
    user: session?.user ?? null,
    isLoading: false,
    // Reset admin status on sign out
    isAdmin: session ? state.isAdmin : null,
  }))

  if (event === 'SIGNED_IN' && session) {
    if (previousUserId !== null && previousUserId !== newUserId) {
      queryClient.invalidateQueries()
    }
  } else if (event === 'SIGNED_OUT' || !session) {
    queryClient.clear()
  }
})

export const authActions = {
  signIn: async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  },
  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },
  setAdminStatus: (isAdmin: boolean) => {
    authStore.setState((state) => ({ ...state, isAdmin }))
  },
}
